/**
 * useTokenManager - PROJ-035: Session Expiration Handling
 *
 * Hook for proactive token management:
 * - Decodes JWT to get expiration time
 * - Schedules automatic refresh before expiration
 * - Emits events when session expires
 * - Synchronizes token refresh across multiple tabs
 */

import { useEffect, useCallback, useRef } from 'react';
import { getAccessToken, getRefreshToken, setAccessToken, setRefreshToken } from '@/lib/auth';
import { tabSync } from '@/lib/tabSync';

interface TokenPayload {
  exp: number;
  iat: number;
  sub: string;
  email?: string;
  // PROJ-035: Sliding session fields
  idleExp?: number;
  absExp?: number;
}

// PROJ-035: Activity tracking configuration
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
const ACTIVITY_THROTTLE_MS = 60000; // Only track once per minute

// Refresh buffer: trigger refresh 2 minutes before expiration
const REFRESH_BUFFER_SECONDS = 120;

// Event names
export const SESSION_EVENTS = {
  EXPIRED: 'session:expired',
  REFRESHED: 'session:refreshed',
  EXPIRING_SOON: 'session:expiring-soon',
  // PROJ-035: Separate events for idle vs absolute expiration
  IDLE_EXPIRING_SOON: 'session:idle-expiring-soon',
  ABSOLUTE_EXPIRING_SOON: 'session:absolute-expiring-soon',
} as const;

// PROJ-035: Interface for token expiration details
export interface TokenExpiration {
  idleExp: number | null;
  absExp: number | null;
  isIdleExpiringSoon: boolean;
  isAbsoluteExpiringSoon: boolean;
  secondsUntilIdle: number | null;
  secondsUntilAbsolute: number | null;
}

// Helper function to throttle activity tracking
function throttle<T extends (...args: any[]) => void>(func: T, wait: number): T {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let lastCall = 0;

  return ((...args: any[]) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    if (timeSinceLastCall >= wait) {
      lastCall = now;
      func(...args);
    } else if (!timeout) {
      const remaining = wait - timeSinceLastCall;
      timeout = setTimeout(() => {
        lastCall = Date.now();
        timeout = null;
        func(...args);
      }, remaining);
    }
  }) as T;
}

export function useTokenManager() {
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  // PROJ-035: Track last activity time
  const lastActivityTimeRef = useRef(Date.now());

  /**
   * Decode JWT without external library
   */
  const decodeToken = useCallback((jwt: string): TokenPayload | null => {
    try {
      const parts = jwt.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      // Handle URL-safe base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('[TokenManager] Failed to decode token:', error);
      return null;
    }
  }, []);

  /**
   * Check if token is expiring soon
   */
  const isTokenExpiringSoon = useCallback((jwt: string, bufferSeconds = REFRESH_BUFFER_SECONDS): boolean => {
    const payload = decodeToken(jwt);
    if (!payload?.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now <= bufferSeconds;
  }, [decodeToken]);

  /**
   * Get seconds until token expires
   */
  const getSecondsUntilExpiry = useCallback((jwt: string): number => {
    const payload = decodeToken(jwt);
    if (!payload?.exp) return 0;

    const now = Math.floor(Date.now() / 1000);
    return Math.max(payload.exp - now, 0);
  }, [decodeToken]);

  /**
   * PROJ-035: Get detailed expiration info for refresh token
   */
  const getTokenExpiration = useCallback((refreshToken: string): TokenExpiration | null => {
    const payload = decodeToken(refreshToken);
    if (!payload) return null;

    const now = Math.floor(Date.now() / 1000);
    const WARNING_THRESHOLD = 5 * 60; // 5 minutes

    const secondsUntilIdle = payload.idleExp ? Math.max(payload.idleExp - now, 0) : null;
    const secondsUntilAbsolute = payload.absExp ? Math.max(payload.absExp - now, 0) : null;

    return {
      idleExp: payload.idleExp || null,
      absExp: payload.absExp || null,
      isIdleExpiringSoon: secondsUntilIdle !== null && secondsUntilIdle <= WARNING_THRESHOLD,
      isAbsoluteExpiringSoon: secondsUntilAbsolute !== null && secondsUntilAbsolute <= WARNING_THRESHOLD,
      secondsUntilIdle,
      secondsUntilAbsolute,
    };
  }, [decodeToken]);

  /**
   * Attempt silent token refresh
   */
  const silentRefresh = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    isRefreshingRef.current = true;

    // Abort any previous in-flight refresh
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal: controller.signal,
      });

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;

        if (data.accessToken) {
          setAccessToken(data.accessToken);

          // If new refresh token provided (rotation), update it
          if (data.refreshToken) {
            setRefreshToken(data.refreshToken);
          }

          window.dispatchEvent(new CustomEvent(SESSION_EVENTS.REFRESHED));
          return true;
        }
      }

      return false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return false; // Cancelled — component unmounting or new refresh superseded
      }
      console.error('[TokenManager] Silent refresh error:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  /**
   * Schedule proactive token refresh
   */
  const scheduleTokenRefresh = useCallback((jwt: string) => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    const payload = decodeToken(jwt);
    if (!payload?.exp) {
      console.warn('[TokenManager] Cannot schedule refresh: invalid token');
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp - now;

    // If already expired, emit event immediately
    if (expiresIn <= 0) {
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
      return;
    }

    // Calculate when to refresh (buffer seconds before expiry)
    const refreshIn = Math.max((expiresIn - REFRESH_BUFFER_SECONDS) * 1000, 0);

    // If token expires very soon, try to refresh immediately
    if (refreshIn === 0) {
      silentRefresh().then((success) => {
        if (!success) {
          window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
        }
      });
      return;
    }

    // Schedule refresh
    refreshTimeoutRef.current = setTimeout(async () => {
      // Emit warning event
      window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRING_SOON));

      const success = await silentRefresh();

      if (success) {
        // Re-schedule for new token
        const newToken = getAccessToken();
        if (newToken) {
          scheduleTokenRefresh(newToken);
        }
      } else {
        // Refresh failed, session expired
        window.dispatchEvent(new CustomEvent(SESSION_EVENTS.EXPIRED));
      }
    }, refreshIn);
  }, [decodeToken, silentRefresh]);

  /**
   * Initialize token management on mount
   */
  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      scheduleTokenRefresh(token);
    }

    // Listen for token changes (e.g., from other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken' && e.newValue) {
        scheduleTokenRefresh(e.newValue);
      }
    };

    // PROJ-035 Enhancement: Listen for TOKEN_REFRESHED from other tabs
    const handleTabTokenRefreshed = (message: any) => {
      if (message.type === 'TOKEN_REFRESHED') {
        const newToken = getAccessToken();
        if (newToken) {
          scheduleTokenRefresh(newToken);
        }
      }
    };

    // PROJ-035 Enhancement: Listen for SESSION_EXPIRED from other tabs
    const handleTabSessionExpired = (message: any) => {
      if (message.type === 'SESSION_EXPIRED') {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    tabSync.on('TOKEN_REFRESHED', handleTabTokenRefreshed);
    tabSync.on('SESSION_EXPIRED', handleTabSessionExpired);

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      // Abort any in-flight refresh request on unmount
      abortControllerRef.current?.abort();
      window.removeEventListener('storage', handleStorageChange);
      tabSync.off('TOKEN_REFRESHED', handleTabTokenRefreshed);
      tabSync.off('SESSION_EXPIRED', handleTabSessionExpired);
    };
  }, [scheduleTokenRefresh]);

  /**
   * PROJ-035: Set up activity tracking
   */
  useEffect(() => {
    // Throttled activity handler
    const handleActivity = throttle(() => {
      lastActivityTimeRef.current = Date.now();

      // Optionally: proactively refresh if close to idle timeout
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const expiration = getTokenExpiration(refreshToken);
        if (expiration?.isIdleExpiringSoon) {
          silentRefresh();
        }
      }
    }, ACTIVITY_THROTTLE_MS);

    // Set up activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [getTokenExpiration, silentRefresh]);

  return {
    decodeToken,
    isTokenExpiringSoon,
    getSecondsUntilExpiry,
    getTokenExpiration, // PROJ-035: Added
    silentRefresh,
    scheduleTokenRefresh,
  };
}

/**
 * Hook to listen for session events
 */
export function useSessionEvents(handlers: {
  onExpired?: () => void;
  onRefreshed?: () => void;
  onExpiringSoon?: () => void;
  // PROJ-035: New handlers
  onIdleExpiringSoon?: () => void;
  onAbsoluteExpiringSoon?: () => void;
}) {
  useEffect(() => {
    const handleExpired = () => handlers.onExpired?.();
    const handleRefreshed = () => handlers.onRefreshed?.();
    const handleExpiringSoon = () => handlers.onExpiringSoon?.();
    const handleIdleExpiringSoon = () => handlers.onIdleExpiringSoon?.();
    const handleAbsoluteExpiringSoon = () => handlers.onAbsoluteExpiringSoon?.();

    if (handlers.onExpired) {
      window.addEventListener(SESSION_EVENTS.EXPIRED, handleExpired);
    }
    if (handlers.onRefreshed) {
      window.addEventListener(SESSION_EVENTS.REFRESHED, handleRefreshed);
    }
    if (handlers.onExpiringSoon) {
      window.addEventListener(SESSION_EVENTS.EXPIRING_SOON, handleExpiringSoon);
    }
    if (handlers.onIdleExpiringSoon) {
      window.addEventListener(SESSION_EVENTS.IDLE_EXPIRING_SOON, handleIdleExpiringSoon);
    }
    if (handlers.onAbsoluteExpiringSoon) {
      window.addEventListener(SESSION_EVENTS.ABSOLUTE_EXPIRING_SOON, handleAbsoluteExpiringSoon);
    }

    return () => {
      window.removeEventListener(SESSION_EVENTS.EXPIRED, handleExpired);
      window.removeEventListener(SESSION_EVENTS.REFRESHED, handleRefreshed);
      window.removeEventListener(SESSION_EVENTS.EXPIRING_SOON, handleExpiringSoon);
      window.removeEventListener(SESSION_EVENTS.IDLE_EXPIRING_SOON, handleIdleExpiringSoon);
      window.removeEventListener(SESSION_EVENTS.ABSOLUTE_EXPIRING_SOON, handleAbsoluteExpiringSoon);
    };
  }, [handlers]);
}
